import {
  DailyUsageSummary,
  WeeklyUsageData,
  BlockStats,
  UsageTrend,
  DailyLimitPrediction,
  getWeekStart,
  getWeekEnd,
  getYesterdayDate,
  calculateTrend,
  formatDateKey,
} from './types';
import { getDailyUsage, getUsageDateRange, getBlockEventsForDateRange } from './queries';

// Hook: Load and analyze usage data for today and week
export async function loadUsageAnalytics(
  familyId: string,
  deviceId: string,
  dailyLimit: number = 120 // default 2 hours in minutes
): Promise<{
  today: DailyUsageSummary | null;
  week: WeeklyUsageData | null;
  blockStats: BlockStats | null;
  trends: UsageTrend[];
  prediction: DailyLimitPrediction | null;
  error: string | null;
}> {
  try {
    const now = new Date();
    const weekStart = getWeekStart(now);
    const weekEnd = getWeekEnd(now);
    const yesterday = getYesterdayDate();

    // Load today's usage
    const today = await getDailyUsage(familyId, deviceId, now);

    // Load this week's usage
    const weekDays = await getUsageDateRange(familyId, deviceId, weekStart, weekEnd);
    const weekTotal = weekDays.reduce((sum, day) => sum + day.totalScreenTime, 0);
    const week: WeeklyUsageData = {
      week: { start: weekStart, end: weekEnd },
      daily: weekDays,
      total: weekTotal,
      averageDaily: weekDays.length > 0 ? weekTotal / weekDays.length : 0,
    };

    // Load and analyze block events
    const blockEvents = await getBlockEventsForDateRange(familyId, deviceId, weekStart, weekEnd);
    const blockStats: BlockStats = {
      totalBlocks: blockEvents.length,
      byReason: {
        schedule: blockEvents.filter(e => e.reason === 'schedule').length,
        limit_reached: blockEvents.filter(e => e.reason === 'limit_reached').length,
        blocklist: blockEvents.filter(e => e.reason === 'blocklist').length,
      },
      byApp: {},
      recent: blockEvents.slice(0, 10),
    };

    blockEvents.forEach(event => {
      blockStats.byApp[event.appName || event.packageName] =
        (blockStats.byApp[event.appName || event.packageName] || 0) + 1;
    });

    // Calculate trends (today vs yesterday)
    const yesterdayUsage = await getDailyUsage(familyId, deviceId, yesterday);
    const trends: UsageTrend[] = [];

    today.apps.forEach(todayApp => {
      const yesterdayApp = yesterdayUsage.apps.find(a => a.packageName === todayApp.packageName);
      const previousUsage = yesterdayApp?.duration || 0;
      const currentUsage = todayApp.duration;
      const delta = currentUsage - previousUsage;
      const percentChange =
        previousUsage === 0
          ? currentUsage > 0
            ? 100
            : 0
          : (delta / previousUsage) * 100;

      trends.push({
        appName: todayApp.appName || todayApp.packageName,
        packageName: todayApp.packageName,
        previous: previousUsage,
        current: currentUsage,
        change: percentChange,
        delta: delta,
        trend: calculateTrend(previousUsage, currentUsage),
      });
    });

    // Sort trends by absolute change
    trends.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

    // Predict daily limit usage
    const prediction = predictDailyLimitExceeded(today.totalScreenTime, dailyLimit);

    return {
      today,
      week,
      blockStats,
      trends,
      prediction,
      error: null,
    };
  } catch (err) {
    return {
      today: null,
      week: null,
      blockStats: null,
      trends: [],
      prediction: null,
      error: (err as Error).message,
    };
  }
}

// Calculate if daily limit will be exceeded
function predictDailyLimitExceeded(
  currentUsage: number,
  dailyLimit: number
): DailyLimitPrediction {
  const percentUsed = (currentUsage / dailyLimit) * 100;
  const now = new Date();
  const hoursUsed = currentUsage / 60; // convert minutes to hours
  const hoursSinceStart = now.getHours() + now.getMinutes() / 60;

  // Simple linear projection: average rate * remaining hours in day
  const hoursSinceStartOfDay = hoursSinceStart; // assume day started at midnight
  const ratePerHour = hoursSinceStartOfDay > 0 ? hoursUsed / hoursSinceStartOfDay : 0;
  const hoursUntilMidnight = 24 - hoursSinceStartOfDay;
  const projectedAdditionalUsage = ratePerHour * hoursUntilMidnight * 60; // convert back to minutes

  const estimatedTotalUsage = currentUsage + projectedAdditionalUsage;
  const willExceed = estimatedTotalUsage > dailyLimit;
  const minutesUntilLimit = Math.max(0, dailyLimit - currentUsage);

  let estimatedTimeUntilLimit: Date | null = null;
  if (willExceed && ratePerHour > 0) {
    const minutesUntilLimitTime = minutesUntilLimit;
    const minutesToLimit = minutesUntilLimitTime / ratePerHour;
    estimatedTimeUntilLimit = new Date(now.getTime() + minutesToLimit * 60 * 1000);
  }

  return {
    currentUsage,
    dailyLimit,
    percentUsed,
    estimatedTotalUsage: Math.round(estimatedTotalUsage),
    willExceed,
    minutesUntilLimit,
    estimatedTimeUntilLimit,
  };
}
